// ============================================================================
// FSA — DISCLOSURE VALUE-SOURCE SYNC
// ============================================================================
// Shared drift-detection + refresh for disclosure `fixed_row`s that declare a
// `value_source` pull (Note 35 EPS profit ← P&L PL_PROFIT_FOR_PERIOD being the
// live example).
//
// Two consumers, one implementation:
//   • DisclosuresPane  — enables/disables "Update from Sources" and applies it
//                        on click.
//   • FsaNoteModal     — folds the same drift into `canSave`, and auto-applies
//                        it on Save (mirrors the ledger-drift path, where the
//                        auditor never needs a separate "Update from Ledger").
//
// Before this module the drift lived only in the pane, so a P&L profit change
// lit up "Update from Sources" but left Save disabled — the note's stale EPS
// could only be persisted by editing some unrelated cell to force the dirty
// flag. See canSave in FsaNoteModal.

import type { DisclosureValueSourceLive } from 'api/types/fsa/noteData.types';
import type { ColumnSpec } from './DisclosureVariableGrid';
import {
  recomputeFixedGridFromSpec,
  type FixedRow,
  type Row as DisclosureRow,
} from './DisclosureFixedGrid';
import type { RoundingFactor } from '../../shared/rounding';

/** Minimal shape this module needs off a disclosure spec entry. */
export interface ValueSourceSpec {
  code: string;
  column_spec?: ColumnSpec[];
  fixed_rows?: Record<string, unknown>[];
  [key: string]: unknown;
}

/** `<disclosure_code>.<row_key>` → live value, grouped per disclosure. */
export type LiveByDisclosure = Record<string, Record<string, DisclosureValueSourceLive>>;

/** Amounts below this are treated as equal (half-paisa float safety). */
const EPSILON = 0.005;

/**
 * Splits the flat `"<disclosure_code>.<row_key>"` dictionary from hydrate into
 * per-disclosure inner maps. Malformed keys (no dot, or a leading dot) are
 * skipped rather than throwing — a bad template row must not break the pane.
 */
export function splitLiveValuesByDisclosure(
  valueSourceLiveValues?: Record<string, DisclosureValueSourceLive> | null,
): LiveByDisclosure {
  const out: LiveByDisclosure = {};
  if (!valueSourceLiveValues) return out;
  for (const [key, live] of Object.entries(valueSourceLiveValues)) {
    const dot = key.indexOf('.');
    if (dot <= 0) continue;
    const disclosureCode = key.slice(0, dot);
    const rowKey = key.slice(dot + 1);
    if (!out[disclosureCode]) out[disclosureCode] = {};
    out[disclosureCode][rowKey] = live;
  }
  return out;
}

/**
 * TRUE when any value-source row's saved amount differs from its source
 * Statement's current amount, in either period. A PY-only move counts —
 * comparatives drift too (prior-year statement re-saved, opening TB replaced).
 */
export function hasValueSourceDrift(
  specs: ValueSourceSpec[],
  additionalDisclosures: Record<string, unknown>,
  liveByDisclosure: LiveByDisclosure,
): boolean {
  for (const spec of specs) {
    const rowsLive = liveByDisclosure[spec.code];
    if (!rowsLive) continue;
    const fixedRows = (spec.fixed_rows ?? []) as Array<{ row_key?: string }>;
    const savedRows = additionalDisclosures?.[spec.code];
    const savedArr = Array.isArray(savedRows) ? savedRows : [];
    for (let i = 0; i < fixedRows.length; i++) {
      const rk = fixedRows[i]?.row_key;
      if (!rk || !rowsLive[rk]) continue;
      const live = rowsLive[rk];
      const saved = savedArr[i] as Record<string, unknown> | undefined;
      const savedActualCy = typeof saved?.value_cy === 'number' ? (saved.value_cy as number) : 0;
      const savedActualPy = typeof saved?.value_py === 'number' ? (saved.value_py as number) : 0;
      if (Math.abs(savedActualCy - live.actualCy) > EPSILON) return true;
      if (Math.abs(savedActualPy - live.actualPy) > EPSILON) return true;
    }
  }
  return false;
}

/**
 * Overwrites every value-source row with its live value and cascades the
 * refreshed operands through the disclosure's computed rows (EPS profit moves
 * → basic_eps / diluted_eps move with it). Rows the auditor owns — share
 * counts, par value, dilutive count — are preserved exactly as saved.
 *
 * Returns one entry per disclosure that actually changed; disclosures with no
 * value-source rows, or already in sync, are omitted so the caller can skip a
 * no-op state update.
 */
export function buildValueSourceRefresh(
  specs: ValueSourceSpec[],
  additionalDisclosures: Record<string, unknown>,
  liveByDisclosure: LiveByDisclosure,
  roundingFactorKey?: string | null,
): Array<{ code: string; rows: DisclosureRow[] }> {
  const updates: Array<{ code: string; rows: DisclosureRow[] }> = [];

  for (const spec of specs) {
    const rowsLive = liveByDisclosure[spec.code];
    if (!rowsLive) continue;
    const fixedRowsSpec = (spec.fixed_rows ?? []) as FixedRow[];
    const colSpec = (spec.column_spec ?? []) as ColumnSpec[];
    const savedRows = additionalDisclosures?.[spec.code];
    const savedArr = Array.isArray(savedRows) ? (savedRows as DisclosureRow[]) : [];

    // Pass 1: overwrite only the value-source rows with their live values.
    const overwritten: DisclosureRow[] = [];
    let mutated = false;
    for (let i = 0; i < fixedRowsSpec.length; i++) {
      const existing = (savedArr[i] ?? {}) as DisclosureRow;
      const rk = fixedRowsSpec[i]?.row_key;
      if (rk && rowsLive[rk]) {
        const live = rowsLive[rk];
        overwritten.push({
          ...existing,
          particulars: (existing.particulars as string)
            ?? (fixedRowsSpec[i]?.particulars as string)
            ?? '',
          value_cy: live.actualCy,
          value_py: live.actualPy,
          value_cy__rounded: live.roundedCy,
          value_py__rounded: live.roundedPy,
        });
        mutated = true;
      } else {
        overwritten.push(existing);
      }
    }
    if (!mutated) continue;

    // Pass 2: cascade into the computed rows that reference them. Without
    // this, EPS profit updates but basic_eps / diluted_eps stay at their
    // pre-refresh values until the auditor edits another cell.
    const recomputed = recomputeFixedGridFromSpec(
      overwritten,
      colSpec,
      fixedRowsSpec,
      (roundingFactorKey as RoundingFactor | null | undefined) ?? null,
      undefined,        // tolerance — default
      true,             // forceFreshComputed: discard prior tolerance-preserved rounded on computed rows
    );
    updates.push({ code: spec.code, rows: recomputed });
  }

  return updates;
}
